/**
 * CollectionCard Component
 * Individual collection card with actions and expandable document list
 */

import { useState } from "react";

import { useCollectionDocuments } from "../../hooks/useCollectionDocuments";
import { useCollectionJobs } from "../../hooks/useCollectionJobs";
import { useTriggerCollectionAction } from "../../hooks/useSync";
import type { CollectionSummary } from "../../types/admin";
import { DocumentList } from "./DocumentList";

interface CollectionCardProps {
  collection: CollectionSummary;
}

const COLLECTION_TYPE_LABELS: Record<string, string> = {
  "google-drive": "🗄️ Google Drive",
  custom: "🧩 Custom",
};

function formatDate(dateString: string | null): string {
  if (!dateString) return "Nooit";
  const date = new Date(dateString);
  return date.toLocaleString("nl-NL");
}

function JobBadge({
  label,
  status,
  done,
  total,
  completed,
  skipped,
  failed,
  skippedUnavailable,
  skippedUnchanged,
  skippedConversionFailed,
}: {
  label: string;
  status: "idle" | "running" | "completed";
  done: number;
  total: number;
  completed: number;
  skipped: number;
  failed: number;
  skippedUnavailable: number;
  skippedUnchanged: number;
  skippedConversionFailed: number;
}) {
  const resolved = completed + skipped + failed;
  const effectiveStatus: "idle" | "running" | "completed" =
    total > 0 && resolved >= total ? "completed" : status;

  let className = "bg-surface-container text-on-surface-variant";
  if (failed > 0) {
    className = "bg-error-container text-on-error-container";
  } else if (effectiveStatus === "running") {
    className = "bg-tertiary-container text-on-tertiary-container";
  } else if (effectiveStatus === "completed") {
    className = "bg-primary-container text-on-primary-container";
  }

  const tooltip = [
    `${label}`,
    `Voltooid: ${completed}`,
    `Overgeslagen: ${skipped}`,
    `Mislukt: ${failed}`,
    `- unavailable: ${skippedUnavailable}`,
    `- unchanged: ${skippedUnchanged}`,
    `- conversion-failed: ${skippedConversionFailed}`,
  ].join("\n");

  return (
    <span className={`px-2 py-1 rounded-sm text-xs font-semibold ${className}`} title={tooltip}>
      {label}: {done}/{total}
    </span>
  );
}

export function CollectionCard({ collection }: CollectionCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const triggerAction = useTriggerCollectionAction();

  const {
    data: documents = [],
    isLoading: documentsLoading,
    error: documentsError,
  } = useCollectionDocuments(collection.collectionId, isExpanded);

  const { data: jobs } = useCollectionJobs(isExpanded ? collection.collectionId : null);

  const handleAction = (action: "discover" | "download" | "convert" | "extract" | "index") => {
    triggerAction.mutate({
      collectionId: collection.collectionId,
      action,
    });
  };

  // Parse config for display
  const config = collection.config;
  const configDetails: Array<{ label: string; value: string }> = [];

  if ("driveId" in config && typeof config["driveId"] === "string") {
    configDetails.push({ label: "Drive ID", value: config["driveId"] });
  }
  if ("folderId" in config && typeof config["folderId"] === "string") {
    configDetails.push({ label: "Folder ID", value: config["folderId"] });
  }
  configDetails.push({ label: "Slug", value: collection.slug });
  if (collection.defaultCategory) {
    configDetails.push({ label: "Standaard categorie", value: collection.defaultCategory });
  }
  if (collection.defaultPriority != null) {
    configDetails.push({
      label: "Standaard prioriteit",
      value: String(collection.defaultPriority),
    });
  }

  return (
    <div className="bg-surface-container-lowest rounded-lg shadow-2 p-6">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-xl font-bold text-on-surface">{collection.name}</h3>
            <span className="bg-primary-container text-on-primary-container px-3 py-1 rounded-full text-sm font-semibold">
              {COLLECTION_TYPE_LABELS[collection.type] ?? collection.type}
            </span>
          </div>
          <p className="text-sm text-on-surface-variant mb-3">
            ID:{" "}
            <code className="bg-surface-container px-2 py-1 rounded-sm">
              {collection.collectionId}
            </code>
          </p>
          <div className="text-sm text-on-surface space-y-1">
            {configDetails.map((detail) => (
              <div key={detail.label}>
                <span className="font-semibold">{detail.label}:</span> {detail.value}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t pt-4 mb-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-on-surface-variant">Aangemaakt:</span>
            <div className="font-semibold">{formatDate(collection.createdAt)}</div>
          </div>
          <div>
            <span className="text-on-surface-variant">Bijgewerkt:</span>
            <div className="font-semibold">{formatDate(collection.updatedAt)}</div>
          </div>
        </div>
        {jobs && (
          <div className="mt-3 flex flex-wrap gap-2">
            <JobBadge
              label="Download"
              status={jobs.download.status}
              done={jobs.download.completed + jobs.download.skipped}
              total={jobs.download.total}
              completed={jobs.download.completed}
              skipped={jobs.download.skipped}
              failed={jobs.download.failed}
              skippedUnavailable={jobs.download.skippedUnavailable}
              skippedUnchanged={jobs.download.skippedUnchanged}
              skippedConversionFailed={jobs.download.skippedConversionFailed}
            />
            <JobBadge
              label="Convert"
              status={jobs.convert.status}
              done={jobs.convert.completed + jobs.convert.skipped}
              total={jobs.convert.total}
              completed={jobs.convert.completed}
              skipped={jobs.convert.skipped}
              failed={jobs.convert.failed}
              skippedUnavailable={jobs.convert.skippedUnavailable}
              skippedUnchanged={jobs.convert.skippedUnchanged}
              skippedConversionFailed={jobs.convert.skippedConversionFailed}
            />
            <JobBadge
              label="Extract"
              status={jobs.extract.status}
              done={jobs.extract.completed + jobs.extract.skipped}
              total={jobs.extract.total}
              completed={jobs.extract.completed}
              skipped={jobs.extract.skipped}
              failed={jobs.extract.failed}
              skippedUnavailable={jobs.extract.skippedUnavailable}
              skippedUnchanged={jobs.extract.skippedUnchanged}
              skippedConversionFailed={jobs.extract.skippedConversionFailed}
            />
            <JobBadge
              label="Index"
              status={jobs.index.status}
              done={jobs.index.completed + jobs.index.skipped}
              total={jobs.index.total}
              completed={jobs.index.completed}
              skipped={jobs.index.skipped}
              failed={jobs.index.failed}
              skippedUnavailable={jobs.index.skippedUnavailable}
              skippedUnchanged={jobs.index.skippedUnchanged}
              skippedConversionFailed={jobs.index.skippedConversionFailed}
            />
          </div>
        )}
      </div>

      {/* Document list expand/collapse section */}
      <div className="border-t pt-4 mb-4">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 text-sm font-medium text-on-surface-variant hover:text-primary transition-colors w-full text-left"
        >
          <span className={`transform transition-transform ${isExpanded ? "rotate-90" : ""}`}>
            ▶
          </span>
          <span>Bekijk documenten</span>
          <span className="bg-surface-container text-on-surface-variant px-2 py-0.5 rounded-full text-xs">
            {isExpanded ? documents.length : "..."}
          </span>
        </button>

        {isExpanded && (
          <div className="mt-3">
            <DocumentList
              documents={documents}
              isLoading={documentsLoading}
              error={documentsError}
            />
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => handleAction("discover")}
          disabled={triggerAction.isPending}
          className="px-3 py-2 bg-surface-container hover:bg-surface-container-high disabled:bg-outline-variant text-on-surface rounded-md text-sm font-semibold transition-colors"
        >
          🔍 Discover
        </button>
        <button
          onClick={() => handleAction("download")}
          disabled={triggerAction.isPending}
          className="px-3 py-2 bg-surface-container hover:bg-surface-container-high disabled:bg-outline-variant text-on-surface rounded-md text-sm font-semibold transition-colors"
        >
          📥 Download
        </button>
        <button
          onClick={() => handleAction("convert")}
          disabled={triggerAction.isPending}
          className="px-3 py-2 bg-surface-container hover:bg-surface-container-high disabled:bg-outline-variant text-on-surface rounded-md text-sm font-semibold transition-colors"
        >
          🔄 Convert
        </button>
        <button
          onClick={() => handleAction("extract")}
          disabled={triggerAction.isPending}
          className="px-3 py-2 bg-surface-container hover:bg-surface-container-high disabled:bg-outline-variant text-on-surface rounded-md text-sm font-semibold transition-colors"
        >
          📝 Extract
        </button>
        <button
          onClick={() => handleAction("index")}
          disabled={triggerAction.isPending}
          className="px-3 py-2 bg-primary hover:brightness-110 disabled:bg-outline-variant text-on-primary rounded-md text-sm font-semibold transition-colors shadow-1"
        >
          📚 Index
        </button>
      </div>
    </div>
  );
}
